import axios from "axios";
import config from "../../../config/config";
import {
  CreateOrderRequest,
  CreateOrderResponse,
  ApplyVoucherRequest,
  ApplyVoucherResponse,
  UpdateCustomerInfoRequest,
  UpdateCustomerInfoResponse,
  CommitOrderRequest,
  CommitOrderResponse,
  GetOrderResponse,
  PaymentMethodDescriptor,
  CardPaymentFormData,
  ProcessCardPaymentResponse,
  ErrorResponse,
} from "./checkout.interfaces";

export class CheckoutError extends Error {
  constructor(
    message: string,
    public readonly errorCode?: string,
    public readonly statusCode?: number
  ) {
    super(message);
    this.name = 'CheckoutError';
  }
}

const API_URL = `${config.apiBaseUrl}/students/checkout`;

const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: config.defaultTimeout,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const apiCreateOrder = async (
  request: CreateOrderRequest
): Promise<CreateOrderResponse> => {
  try {
    const response = await axiosClient.post<CreateOrderResponse>("/orders", request);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data as ErrorResponse;
      throw new CheckoutError(
        errorData.message || "Failed to create order",
        errorData.errorCode,
        error.response.status
      );
    }
    throw error;
  }
};

export const apiApplyVoucher = async (
  request: ApplyVoucherRequest
): Promise<ApplyVoucherResponse> => {
  try {
    const response = await axiosClient.patch<ApplyVoucherResponse>(
      `/orders/${request.orderId}/voucher`,
      { voucherCode: request.voucherCode }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data as ErrorResponse;
      throw new CheckoutError(
        errorData.message || "Não foi possível aplicar o cupom",
        errorData.errorCode,
        error.response.status
      );
    }
    throw error;
  }
};

export const apiUpdateCustomerInfo = async (
  request: UpdateCustomerInfoRequest
): Promise<UpdateCustomerInfoResponse> => {
  try {
    const response = await axiosClient.patch<UpdateCustomerInfoResponse>(
      `/orders/${request.orderId}/customer`,
      { customerInfo: request.customerInfo }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || "Failed to update customer information");
    }
    throw error;
  }
};

export const apiGetPaymentMethods = async (): Promise<PaymentMethodDescriptor[]> => {
  try {
    const response = await axiosClient.get<PaymentMethodDescriptor[]>("/payment-methods");
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || "Failed to load payment methods");
    }
    throw error;
  }
};

export const apiGetOrder = async (orderId: string): Promise<GetOrderResponse> => {
  try {
    const response = await axiosClient.get<GetOrderResponse>(`/orders/${orderId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || "Failed to get order");
    }
    throw error;
  }
};

export const apiCommitOrder = async (
  request: CommitOrderRequest
): Promise<CommitOrderResponse> => {
  try {
    const response = await axiosClient.patch<CommitOrderResponse>(
      `/orders/${request.orderId}/commit`,
      { provider: request.provider, method: request.method }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || "Failed to commit order");
    }
    throw error;
  }
};

export const apiProcessCardPayment = async (
  orderId: string,
  form: CardPaymentFormData
): Promise<ProcessCardPaymentResponse> => {
  try {
    const response = await axiosClient.post<ProcessCardPaymentResponse>(
      `/orders/${orderId}/process-payment`,
      form
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const data = error.response.data as ProcessCardPaymentResponse & ErrorResponse;
      throw new CheckoutError(
        data?.message || "Failed to process card payment",
        data?.errorCode,
        error.response.status
      );
    }
    throw error;
  }
};
